import React, { useState, useEffect } from "react";
import { snackbar } from "../../utils";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createChannelLanguage,
  updateChannelLanguage,
} from "../../Components/service/admin";

const AddChannelLanguage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { editMode, languageData, languageId } = location.state || {};

  const [formData, setFormData] = useState({
    name: "",
    englishName: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Set form data if in edit mode
  useEffect(() => {
    if (editMode && languageData) {
      setFormData({
        name: languageData.name || "",
        englishName: languageData.englishName || "",
      });
    }
  }, [editMode, languageData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Language name is required";
    }

    if (!formData.englishName.trim()) {
      newErrors.englishName = "English name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let result;

      if (editMode) {
        result = await updateChannelLanguage(languageId, formData);
      } else {
        result = await createChannelLanguage(formData);
      }

      if (result.data.error === false) {
        snackbar.success(
          editMode
            ? "Channel language updated successfully"
            : "Channel language added successfully"
        );

        if (!editMode) {
          setFormData({
            name: "",
            englishName: "",
          });
        }

        navigate(-1);
      } else {
        snackbar.error(
          result?.data?.data?.errorMessage ||
            `Failed to ${editMode ? "update" : "add"} channel language`
        );
      }
    } catch (error) {
      snackbar.error(
        `An error occurred while ${
          editMode ? "updating" : "adding"
        } the language`
      );
      console.error(
        `Error ${editMode ? "updating" : "adding"} language:`,
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <div style={{ padding: "20px", maxWidth: "600px", margin: "0 auto" }}>
      <div style={{ marginBottom: "30px" }}>
        <h2 style={{ marginBottom: "8px", color: "#333" }}>
          {editMode ? "Edit Channel Language" : "Add Channel Language"}
        </h2>
        <p style={{ color: "#666", margin: 0 }}>
          {editMode
            ? "Update the channel language information"
            : "Create a new channel language entry"}
        </p>
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          padding: "30px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: "20px" }}>
            <label
              htmlFor="name"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#333",
              }}
            >
              Language Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter language"
              style={{
                width: "100%",
                padding: "12px",
                border: errors.name ? "2px solid #dc3545" : "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
                boxSizing: "border-box",
                outline: "none",
                transition: "border-color 0.2s ease",
              }}
              onFocus={(e) => {
                if (!errors.name) {
                  e.target.style.borderColor = "#007bff";
                }
              }}
              onBlur={(e) => {
                if (!errors.name) {
                  e.target.style.borderColor = "#ddd";
                }
              }}
            />
            {errors.name && (
              <span
                style={{
                  color: "#dc3545",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {errors.name}
              </span>
            )}
          </div>

          <div style={{ marginBottom: "30px" }}>
            <label
              htmlFor="englishName"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#333",
              }}
            >
              English Name *
            </label>
            <input
              type="text"
              id="englishName"
              name="englishName"
              value={formData.englishName}
              onChange={handleInputChange}
              placeholder="Enter English name"
              style={{
                width: "100%",
                padding: "12px",
                border: errors.englishName
                  ? "2px solid #dc3545"
                  : "1px solid #ddd",
                borderRadius: "4px",
                fontSize: "14px",
                boxSizing: "border-box",
                outline: "none",
                transition: "border-color 0.2s ease",
              }}
              onFocus={(e) => {
                if (!errors.englishName) {
                  e.target.style.borderColor = "#007bff";
                }
              }}
              onBlur={(e) => {
                if (!errors.englishName) {
                  e.target.style.borderColor = "#ddd";
                }
              }}
            />
            {errors.englishName && (
              <span
                style={{
                  color: "#dc3545",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {errors.englishName}
              </span>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={handleCancel}
              style={{
                padding: "12px 24px",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
                transition: "background-color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#5a6268";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#6c757d";
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px 24px",
                backgroundColor: loading ? "#ccc" : "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: loading ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "500",
                transition: "background-color 0.2s ease",
                minWidth: "120px",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = "#0056b3";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = "#007bff";
                }
              }}
            >
              {loading
                ? editMode
                  ? "Updating..."
                  : "Adding..."
                : editMode
                ? "Update Language"
                : "Add Language"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddChannelLanguage;
